// Маркерът „Не стига" на ред-рецепта: кои съставки не стигат, колко трябват и
// колко има КЪМ ДАТАТА („Домати — нужни 1,200 кг (тук 0,450) · налични 0,500 кг"
// — „тук" е делът на реда, когато съставката се иска и от други редове) и
// естествените следващи ходове: „Намали на N порции" (колкото стигат при
// останалите редове) и „Раздели на продукти" (за да се поправят поотделно).
// В решетката е лента под реда (извън стрелковата навигация, като лентата на
// разделена рецепта); в картите — кутия в картата. От клавиатурата същите
// ходове дава диалогът при Ctrl+Enter (WriteoffShortageDialog).
import { ExclamationCircleOutlined, SplitCellsOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatQty } from "../../components/qty";
import { portionsLabel } from "./writeoffConfirms";
import {
  lineShareOf,
  shortageQuantities,
  type StockShortage,
} from "./writeoffStockCheck";
import { parseDecimal, type WriteoffLineDraft } from "./writeoffWorksheetShared";

const ACTION_CLASS =
  "inline-flex h-[26px] shrink-0 cursor-pointer items-center gap-[5px] whitespace-nowrap rounded-[7px] border border-shade-3 bg-surface px-2.5 text-[11.5px] font-bold text-danger-ink hover:bg-panel";

type Props = {
  line: WriteoffLineDraft;
  shortages: StockShortage[];
  // колко цели порции стигат (null — не се знае; 0 — не стига и за една)
  fittingPortions: number | null;
  onFitPortions: (key: number, portions: number) => void;
  onSplitRecipe: (key: number) => void;
  variant: "row" | "card";
};

// „нужни 1,200 кг (тук 0,450) · налични 0,500 кг" — делът на реда само когато
// съставката се иска и от други редове
export const ShortageAmounts = ({ shortage, lineKey }: { shortage: StockShortage; lineKey: number }) => {
  const { t } = useTranslation();
  const { needed, available } = shortageQuantities(shortage);
  const own = lineShareOf(shortage, lineKey);
  const shared = shortage.sources.some((source) => source.lineKey !== lineKey);
  return (
    <>
      {t("inventoryPage.wr6Needed")} <span className="font-sheet font-semibold">{needed}</span>
      {shared && (
        <span className="text-body">
          {" "}
          ({t("inventoryPage.wr6OwnShare", { value: formatQty(own, shortage.decimals) })})
        </span>
      )}
      {" · "}
      {t("inventoryPage.wr6Available")} <span className="font-sheet font-semibold">{available}</span>
    </>
  );
};

export const WriteoffShortageBand = ({
  line,
  shortages,
  fittingPortions,
  onFitPortions,
  onSplitRecipe,
  variant,
}: Props) => {
  const { t } = useTranslation();
  if (!shortages.length) return null;
  const portions = parseDecimal(line.quantity);
  const canFit = fittingPortions != null && fittingPortions > 0 && fittingPortions < portions;
  return (
    <div
      role="status"
      data-testid={`wox-shortage-${line.key}`}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-danger-tint text-[11.5px] leading-[1.35] text-danger-ink ${
        variant === "row"
          ? "border-b border-l-[3px] border-shade-2 border-l-danger px-3 py-[7px]"
          : "rounded-lg px-3 py-2"
      }`}
    >
      <span className="inline-flex items-center gap-1.5 font-bold">
        <ExclamationCircleOutlined className="text-[12px]" />
        {t("inventoryPage.wr6BandTitle", { portions: portionsLabel(t, portions) })}
      </span>
      {shortages.map((shortage) => (
        <span key={shortage.productId} className="min-w-0">
          <span className="font-bold">{shortage.name}</span> — <ShortageAmounts shortage={shortage} lineKey={line.key} />
        </span>
      ))}
      <span className="ml-auto flex flex-wrap gap-1.5">
        {canFit && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => onFitPortions(line.key, fittingPortions)}
            className={ACTION_CLASS}
          >
            {t("inventoryPage.wr6FitAction", { portions: portionsLabel(t, fittingPortions) })}
          </button>
        )}
        <button type="button" tabIndex={-1} onClick={() => onSplitRecipe(line.key)} className={ACTION_CLASS}>
          <SplitCellsOutlined className="text-[11px]" />
          {t("inventoryPage.splitToProducts")}
        </button>
      </span>
    </div>
  );
};

export default WriteoffShortageBand;
